import { MouseEvent, FormEvent, useState } from 'react';
import { validateCredentials } from '../../helpers';
import { useTracking } from '../../hooks/useTracking';
import * as S from './styled';
import Modal from '../Modal';

const signUpLink : string = 'https://app.laterpay.net';

const ExternalLink = (props: {
	url: string,
	text: string
}) => {
	return (
		<a href={props.url} target="_blank" rel="noreferrer">
			{props.text}
		</a>
	)
}

const InfoModal = (props: {
	onPrimaryButtonClick: () => void
	onClose: () => void
}) => {
	const [event] = useTracking();

	function handleSignupClick() {
		event({
			category: 'LP RevGen Account',
			action: 'Connect Account',
			label: 'Signup'
		});

		window.open(signUpLink);
	}

	return (
		<Modal title="You're almost done!" onClose={props.onClose}>
			<p>To make sure you get your revenues, we need you to connect your account.</p>

			<button className="rgModalButton primary" onClick={props.onPrimaryButtonClick}>Connect Account</button>
			<button className="rgModalButton" onClick={() => handleSignupClick()}>Signup</button>

			<p>
				<ExternalLink url="https://support.laterpay.net/what-is-laterpay/" text="Learn more" />
			</p>
		</Modal>
	)
}

const ConnectModal = (props: {
	onSuccess: () => void,
	onFailure: () => void,
	onClose: () => void
}) => {
	const [event] = useTracking();
	const [clientID, setClientID] = useState('');
	const [clientSecret, setClientSecret] = useState('');
	const [submitted, setSubmitted] = useState(false);

	function handleCredentialsSubmit(e: FormEvent) {
		e.preventDefault();

		setSubmitted(true);

		validateCredentials(clientID, clientSecret)
			.then(status => {
				status === 400 ? props.onFailure() : props.onSuccess();

				event({
					category: 'LP RevGen Account',
					action: 'Connect Account',
					label: status === 400 ? 'Failure' : 'Success'
				});
			});
	}

	return (
		<Modal title="Connect your account to go live" onClose={props.onClose}>
			{!submitted ?
				<div>
					<p>Unsure where to find this information? <a href="https://docs.laterpay.net/" target="_blank" rel="noreferrer">Click here.</a></p>

					<form onSubmit={handleCredentialsSubmit}>
						<input type="text" placeholder="Client ID" maxLength={43} value={clientID} onChange={(e) => setClientID(e.target.value)} />
						<input type="text" placeholder="Client Secret" maxLength={26} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />

						<input type="submit" className="primary" value={submitted ? "Please wait…" : "Connect Account"} disabled={submitted || !clientID || !clientSecret} />
					</form>

					<p>
						Don't have an account? <a href={signUpLink} target="_blank" rel="noreferrer">Sign up</a>
					</p>
				</div>
			: <S.Loading />
			}

		</Modal>
	)
}

const ErrorModal = (props: {
	onReset: () => void,
	onClose: () => void
}) => {
	function handleReset(e: MouseEvent<HTMLAnchorElement>) {
		e.preventDefault();

		props.onReset();
	}

	return (
		<Modal title="Sorry, something went wrong." onClose={props.onClose}>
			<S.ErrorIcon>!</S.ErrorIcon>
			<p>
				It looks like you need to create an account. Please <ExternalLink url={signUpLink} text="sign up here" />, <a href="#reset" onClick={handleReset}>try again</a>, or contact <a href="mailto:integration@laterpay.net">integration@laterpay.net</a> if you’re still experiencing difficulties.
			</p>
		</Modal>
	)
}

const SuccessModal = (props: {onClose: () => void}) => {
	return (
		<Modal title="Success!" onClose={props.onClose}>
			<p>You have successfully connected your Laterpay account.</p>
		</Modal>
	)
}

const Onboarding = (props: {
	active: boolean,
	onComplete: () => void,
	onCancel: () => void
}) => {
	const defaultModal : string = 'info';
	const [activeModal, setActiveModal] = useState<string>(defaultModal);

	function handleSuccessResponse() {
		setActiveModal('');
		props.onComplete();
	}

	function handleCancel() {
		setActiveModal(defaultModal);
		props.onCancel();
	}

	if (!props.active) {
		return null;
	}

	return (
		<>
			<S.GlobalStyle />

			{activeModal === 'info' && <InfoModal
				onPrimaryButtonClick={() => setActiveModal('connect')}
				onClose={handleCancel}
			/>
			}
			{activeModal === 'connect' && <ConnectModal
				onSuccess={handleSuccessResponse}
				onFailure={() => setActiveModal('error')}
				onClose={handleCancel}
			/>
			}
			{activeModal === 'error' && <ErrorModal
				onReset={() => setActiveModal('connect')}
				onClose={handleCancel}
			/>
			}
			{activeModal === 'success' && <SuccessModal onClose={handleCancel} />}
		</>
	)
}

export default Onboarding;
